import { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import Button from "../../../components/Button";
import { baseURL } from "../../../api/api";
import { Reconciliation } from "../../../api/types";
import usePostReconciliation from "../../../api/usePostReconciliation";
import notification from "../../../utils/notification";
import { typedSxMap } from "../../../utils/typedSxMap";
import style from "../../forms/style/Form.module.scss";

type RerunReconciliationFormProps = {
  reconciliation?: Reconciliation;
  onSuccess: () => void;
  onClose: () => void;
};

const styles = typedSxMap({
  actionButtons: {
    display: "flex",
    gap: "var(--m)",
    justifyContent: "flex-end",
    marginTop: "var(--m)",
  },
});

export default function RerunReconciliationForm({ reconciliation, onSuccess, onClose }: RerunReconciliationFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { mutate, isLoading: isStatusUpdating, isSuccess } = usePostReconciliation(reconciliation?.workspace_id);

  useEffect(() => {
    if (isSuccess && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  const handleRerun = async () => {
    if (!reconciliation) return;

    try {
      setIsLoading(true);

      // Download file 1 original
      const file1Response = await fetch(`${baseURL}pipeline-import/${reconciliation.pipeline_import_id_1}/download-original`, {
        method: "GET",
        credentials: "include",
      });

      if (!file1Response.ok) {
        const res = await file1Response.json();
        throw res.error ? res.error : "Failed to download the first original file";
      }

      const file1Blob = await file1Response.blob();
      const contentType1 = file1Response.headers.get("Content-Type") || "";
      const filename1 =
        reconciliation.pipeline_import_1?.src_file_name || `${reconciliation.pipeline_import_id_1}${getExtensionFromContentType(contentType1)}`;
      const file1 = new File([file1Blob], filename1, { type: contentType1 });

      // Download file 2 original
      const file2Response = await fetch(`${baseURL}pipeline-import/${reconciliation.pipeline_import_id_2}/download-original`, {
        method: "GET",
        credentials: "include",
      });

      if (!file2Response.ok) {
        const res = await file2Response.json();
        throw res.error ? res.error : "Failed to download the second original file";
      }

      const file2Blob = await file2Response.blob();
      const contentType2 = file2Response.headers.get("Content-Type") || "";
      const filename2 =
        reconciliation.pipeline_import_2?.src_file_name || `${reconciliation.pipeline_import_id_2}${getExtensionFromContentType(contentType2)}`;
      const file2 = new File([file2Blob], filename2, { type: contentType2 });

      // Create form data for reconciliation upload
      const formData = new FormData();
      formData.append("file1", file1, file1.name);
      formData.append("file2", file2, file2.name);

      // Generate new name with incremented number
      const newName = generateIncrementedName(reconciliation.name);
      formData.append("name", newName);

      // Upload files for reconciliation
      const uploadResponse = await fetch(`${baseURL}reconciliations/${reconciliation.workspace_id}/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw errorData.err || "Failed to rerun reconciliation";
      }

      // Update the original reconciliation status to "rerun"
      mutate({
        id: reconciliation.id,
        status: "rerun",
      });
    } catch (error) {
      console.error("Error rerunning reconciliation:", error);
      notification({
        title: "Error",
        message: error instanceof Error ? error.message : String(error),
        type: "error",
      });
      setIsLoading(false);
      onClose();
    }
  };

  // Helper function to generate incremented name
  const generateIncrementedName = (originalName: string): string => {
    // Check if name already ends with a number in parentheses like "Name (2)"
    const match = originalName.match(/^(.*)\s\((\d+)\)$/);

    if (match) {
      // Extract base name and current number
      const baseName = match[1];
      const currentNumber = parseInt(match[2], 10);
      // Increment the number
      return `${baseName} (${currentNumber + 1})`;
    } else {
      // If no number in parentheses, add (2)
      return `${originalName} (2)`;
    }
  };

  // Helper function to extract filename from Content-Disposition header
  const getFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
    if (!contentDisposition) return null;

    const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].trim().replace(/"/g, "");
    }
    return null;
  };

  // Helper function to get file extension from content type
  const getExtensionFromContentType = (contentType: string): string => {
    switch (contentType) {
      case "text/csv":
        return ".csv";
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        return ".xlsx";
      default:
        return "";
    }
  };

  return (
    <div className={style.container}>
      <div className={style.title}>
        <h2>Rerun Reconciliation</h2>
      </div>
      <p>Are you sure you want to rerun this reconciliation? A new reconciliation will be created with the original files.</p>
      <fieldset disabled={isLoading || isStatusUpdating}>
        <Box sx={styles.actionButtons}>
          <Button type="button" onClick={onClose} disabled={isLoading || isStatusUpdating}>
            Cancel
          </Button>
          <Button variants={["primary"]} type="button" onClick={handleRerun} disabled={isLoading || isStatusUpdating}>
            Rerun
          </Button>
        </Box>
      </fieldset>
    </div>
  );
}
